<template>
  <div
    v-if="technician"
    :class="[
      'flex gap-4 w-full',
      `resource-cell-${technician.id}`,
      `resource-cell-${technician.user_id}`,
      { 'ml-4': !collapsed }
    ]"
  >
    <div class="flex-shrink-0">
      <Avatar
        :alt="technician.full_name"
        :src="technician.avatar.url"
        :status="technician.is_clocked_in ? 'online' : 'offline'"
        :initials="getInitials(technician)"
      />
    </div>

    <div
      v-if="!collapsed"
      class="shrink-0 flex flex-col truncate text-left w-[65%]"
    >
      <a
        class="truncate font-medium text-gray-900 w-full"
        :title="technician.full_name"
        :href="link ? technician.show_path : null"
      >
        {{ technician.full_name }}
        <a
          v-if="!link"
          :href="technician.show_path"
          class="ml-1 text-xxs relative -top-[2px] text-gray-500"
        >
          <i class="fa-regular fa-arrow-up-right-from-square" />
        </a>
      </a>
      <div
        class="text-xs text-gray-500 font-normal w-full"
        :title="technician.department?.name"
      >
        <div class="truncate w-full">
          {{ technicianLevelLabel(technician) || "--" }}
          <span v-if="technician.department?.name">•</span>
          {{ technician.department?.name }}
        </div>
      </div>
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useStore } from "vuex";

import Avatar from "@/components/Seranova/Indicator/Avatar.vue";
import { getInitials } from "@/mixins";
import { ResourceAPIResponse, TimelineResource } from "@/typings/scheduler";
import { Technician } from "@/typings/technician";
import { technicianLevelLabel } from "./technicianLevel";

/**
 * Timeline draws this through `getComponentHtml`, which mounts it as its own
 * Vue app carrying nothing but the vuex store. A `useEventCalendar()` call here
 * injects nothing, so it rebuilds the whole hook — watchers included — once
 * per resource cell, per repaint of the desktop board. The hook's `resources`
 * is store-derived anyway, so reading the roster off the store is the same
 * answer without the reconstruction.
 */
const store = useStore();
const props = withDefaults(
  defineProps<{
    resource: TimelineResource;
    collapsed?: boolean;
    link?: boolean;
    technicianData?: Technician;
  }>(),
  { link: true, collapsed: false }
);
const technician = computed(() => {
  const found = store.state.timeline?.resources?.find(
    (resource: ResourceAPIResponse) => resource.id === props.resource.id
  ) as ResourceAPIResponse | undefined;
  if (found) return found;
  // The Unassigned row is synthesised by the calendar hook rather than held on
  // the store roster, so for that card the object the caller passed down is the
  // only place its name and avatar exist.
  const passed = props.resource as unknown as Partial<ResourceAPIResponse>;
  if (passed.full_name) return passed as ResourceAPIResponse;
  // Fallback to technicianData prop when resource not found in scheduler resources
  if (props.technicianData) {
    return {
      id: props.technicianData.technician_id,
      user_id: props.technicianData.user_id,
      full_name: props.technicianData.full_name || "",
      avatar: props.technicianData.avatar || { url: "" },
      department: props.technicianData.department,
      installer: props.technicianData.installer || false,
      skill_level: props.technicianData.skill_level,
      is_clocked_in: props.technicianData.is_clocked_in,
      show_path: props.technicianData.show_path
    } as ResourceAPIResponse;
  }
  return undefined;
});
</script>

<style scoped></style>
